import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/header/Header";
import Footer from "../components/footer";
import Gotop from "../components/gotop";
import { authAPI } from "../services/api";
import "./Login.scss";

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing reset link. Please request a new password reset.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Invalid or missing reset link.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!/\d/.test(password)) {
      setError("Password must contain at least one number.");
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      setError("Password must contain at least one special character.");
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.resetPassword(token, password);
      if (response.data.success) {
        setSuccess(true);
        setError("");
        setTimeout(() => navigate("/login"), 3000);
      } else {
        setError(response.data.message || "Failed to reset password.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Invalid or expired reset link. Please request a new password reset."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <>
        <Header />
        <section className="login-page-section">
          <div className="login-page-container">
            <div className="login-page-content">
              <div className="account-section">
                <div className="tf-container">
                  <div className="row">
                    <div className="wd-form-login" style={{ width: "100%" }}>
                      <div className="login-header-text">
                        <h4>Invalid reset link</h4>
                        <p>
                          This password reset link is invalid or has expired.
                          Please request a new one.
                        </p>
                      </div>
                      {error && (
                        <div className="alert alert-danger">{error}</div>
                      )}
                      <div className="sign-up" style={{ marginBottom: "8px" }}>
                        <Link to="/forgot-password">Request new reset link</Link>
                      </div>
                      <Link to="/login" className="back-button">
                        <i className="icon-keyboard_arrow_left" />
                        Back to Login
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <Footer />
        <Gotop />
      </>
    );
  }

  return (
    <>
      <Header />
      <section className="login-page-section">
        <div className="login-page-container">
          <div className="login-page-content">
            <div className="account-section">
              <div className="tf-container">
                <div className="row">
                  <div className="wd-form-login" style={{ width: "100%" }}>
                    <div className="login-header-text">
                      <h4>Set new password</h4>
                      <p>
                        Enter your new password below. Use at least 8 characters
                        with one number and one special character.
                      </p>
                    </div>
                    {error && (
                      <div className="alert alert-danger">{error}</div>
                    )}
                    {success ? (
                      <div className="nofi-form">
                        <p>
                          Your password has been reset. You can now log in with
                          your new password.
                        </p>
                        <p style={{ marginTop: "12px" }}>
                          Redirecting to login...
                        </p>
                        <Link to="/login" className="back-button">
                          <i className="icon-keyboard_arrow_left" />
                          Back to Login
                        </Link>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit}>
                        <div className="ip">
                          <label>
                            New password<span>*</span>
                          </label>
                          <div className="inputs-group auth-pass-inputgroup">
                            <input
                              type={showPass ? "text" : "password"}
                              className="input-form password-input"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              placeholder="New password"
                              required
                              minLength={8}
                            />
                            <span
                              className={`password-addon ${
                                showPass ? "icon-eye" : "icon-eye-off"
                              }`}
                              onClick={(e) => {
                                e.preventDefault();
                                setShowPass(!showPass);
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) =>
                                e.key === "Enter" && setShowPass(!showPass)
                              }
                            />
                          </div>
                        </div>
                        <div className="ip">
                          <label>
                            Confirm password<span>*</span>
                          </label>
                          <input
                            type={showPass ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) =>
                              setConfirmPassword(e.target.value)
                            }
                            placeholder="Confirm new password"
                            required
                            minLength={8}
                          />
                        </div>
                        <button type="submit" disabled={loading}>
                          {loading ? "Resetting..." : "Reset Password"}
                        </button>
                        <Link to="/login" className="back-button">
                          <i className="icon-keyboard_arrow_left" />
                          Back to Login
                        </Link>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
      <Gotop />
    </>
  );
}

export default ResetPassword;
